import React, { useEffect, useState, useCallback } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { isRtlLanguage } from "./Utils";

/**
 * Reusable modal to view + edit the machine translations of one content entity
 * (section / folder / surveyItem / journalItem / survey / journal / patientMessage / contactInformation) in any enabled
 * target language. Backed by the web-service TranslationEditController:
 *   GET  /my-jeeva-api/translation-edit/languages
 *   GET  /my-jeeva-api/translation-edit/{entityType}/{entityId}?language=xx[&procedureId=xx]
 *   PUT  /my-jeeva-api/translation-edit/{entityType}/{entityId}?language=xx[&procedureId=xx]
 *
 * Props:
 *   entityType   string  one of section|folder|surveyItem|journalItem|survey|journal|patientMessage|contactInformation
 *   entityId     string  UUID of the English source entity
 *   fields       array   [{ name, label, rows?, rich? }]  editable text fields
 *                        rich:true -> rich-text (Draft.js) editor, stored as HTML.
 *   sourceFields object  {fieldName: sourceContent} for auto-translation
 *   title        string  header context (optional)
 *   procedureId  string  UUID of the procedure (optional, for language filtering)
 *   onClose      fn
 *
 * The English source is intentionally NOT shown here — it is visible on the
 * content screen behind this modal.
 *
 * If procedureId is provided, only languages enabled for that procedure are shown.
 */
function authHeaders(extra) {
  return Object.assign(
    { Authorization: "Bearer " + (localStorage.getItem("access_token") || "") },
    extra || {}
  );
}

const FALLBACK_LANGUAGES = [
  { code: "es", displayName: "Español", englishName: "Spanish", rtl: false },
  { code: "fr", displayName: "Français", englishName: "French", rtl: false },
  { code: "de", displayName: "Deutsch", englishName: "German", rtl: false },
  { code: "ar", displayName: "العربية", englishName: "Arabic", rtl: true },
];

const ALL_LANGUAGES = [
  { code: "EN", displayName: "English", englishName: "English" },
  { code: "ES", displayName: "Español", englishName: "Spanish" },
  { code: "FR", displayName: "Français", englishName: "French" },
  { code: "DE", displayName: "Deutsch", englishName: "German" },
  { code: "AR", displayName: "العربية", englishName: "Arabic" },
];

function htmlToEditorState(html) {
  const block = htmlToDraft(html || "");
  const contentState = ContentState.createFromBlockArray(block.contentBlocks, block.entityMap);
  return EditorState.createWithContent(contentState);
}

function editorStateToHtml(editorState) {
  if (!editorState) return "";
  return draftToHtml(convertToRaw(editorState.getCurrentContent()));
}

export default function TranslationPanel({ entityType, entityId, fields, title, procedureId, sourceFields, onClose }) {
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState("");
  const [values, setValues] = useState({}); // plain fields
  const [editorStates, setEditorStates] = useState({}); // rich fields
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const [status, setStatus] = useState("");
  const [languagesError, setLanguagesError] = useState("");

  // Load available languages (procedure-specific if procedureId provided)
  useEffect(() => {
    if (!procedureId) {
      setLanguagesError("Please enable languages for this treatment on Translation Management Page.");
      setLanguages([]);
      return;
    }

    const url = `/my-jeeva-api/procedure/${procedureId}/languages`;

    fetch(url, { headers: authHeaders() })
      .then((r) => r.json())
      .then((res) => {
        // Handle both response formats
        const all = (res && Array.isArray(res) && res) ||
                    (res && res.result && Array.isArray(res.result) && res.result) ||
                    [];
        // Filter for enabled languages (excluding English)
        const enabledCodes = all
          .filter((lang) => lang.isEnabled === true || lang.isEnabled === "true")
          .filter((lang) => lang.code.toUpperCase() !== "EN");

        if (enabledCodes.length === 0) {
          setLanguagesError("No languages enabled for this treatment. Please enable them on Translation Management Page.");
          setLanguages([]);
        } else {
          setLanguagesError("");
          setLanguages(enabledCodes);
          setLanguage((prev) => prev || (enabledCodes[0] && enabledCodes[0].code.toLowerCase()) || "es");
        }
      })
      .catch((err) => {
        console.error("Error loading languages:", err);
        setLanguagesError("Failed to load enabled languages. Please refresh.");
        setLanguages([]);
      });
  }, [procedureId]);

  // The API returns upper-case codes while the selected language is lower case, so compare
  // case-insensitively, and fall back to the known RTL list if the flag is missing
  const isRtl = !!(languages.find((l) => l.code.toUpperCase() === language.toUpperCase()) || {}).rtl
    || isRtlLanguage(language);

  const autoTranslate = useCallback(() => {
    if (!language || !sourceFields || Object.keys(sourceFields).length === 0) return;

    setAutoTranslating(true);
    const promises = [];
    const fieldNames = [];

    fields.forEach((fld) => {
      const sourceText = sourceFields[fld.name];
      if (!sourceText || sourceText.trim() === "") return;

      fieldNames.push(fld.name);
      const promise = fetch(
        `/my-jeeva-api/translation-edit/auto-translate?language=${language.toLowerCase()}&sourceText=${encodeURIComponent(sourceText)}`,
        { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }) }
      )
        .then((r) => r.json())
        .then((res) => {
          console.log("Auto-translate response:", res);
          return (res && res.result && res.result.translatedText) || "";
        })
        .catch((err) => {
          console.error(`Error auto-translating:`, err);
          return "";
        });

      promises.push(promise);
    });

    if (promises.length === 0) {
      setAutoTranslating(false);
      return;
    }

    Promise.all(promises).then((results) => {
      const plain = {};
      const rich = {};
      fields.forEach((fld, idx) => {
        const translatedText = results[idx] || "";
        if (fld.rich) rich[fld.name] = htmlToEditorState(translatedText);
        else plain[fld.name] = translatedText;
      });
      setValues(plain);
      setEditorStates(rich);
      setAutoTranslating(false);
    });
  }, [language, sourceFields, fields]);

  const load = useCallback(() => {
    if (!language || !entityId) return;
    setLoading(true);
    setStatus("");

    let url = "/my-jeeva-api/translation-edit/" + entityType + "/" + entityId + "?language=" + language;
    if (procedureId) {
      url += "&procedureId=" + procedureId;
    }

    fetch(url, { headers: authHeaders() })
      .then((r) => r.json())
      .then((res) => {
        // Handle both response formats (with/without success flag)
        const f = (res && res.success && res.result && res.result.fields) ||
                  (res && res.fields) ||
                  {};
        const plain = {};
        const rich = {};
        let hasAnyTranslation = false;

        fields.forEach((fld) => {
          const value = f[fld.name];
          const hasValue = value && value.trim && value.trim() !== "";
          if (hasValue) {
            hasAnyTranslation = true;
          }
          if (fld.rich) rich[fld.name] = htmlToEditorState(value || "");
          else plain[fld.name] = value || "";
        });

        setValues(plain);
        setEditorStates(rich);

        // If no translation exists, show message
        if (!hasAnyTranslation) {
          setStatus("No translation available for this language");
        }
      })
      .catch((err) => {
        console.error("Error loading translation:", err);
        setStatus("Could not load translation.");
      })
      .finally(() => setLoading(false));
  }, [language, entityId, entityType, fields, procedureId, sourceFields, autoTranslate]);

  useEffect(() => {
    load();
  }, [load]);

  const save = () => {
    if (!language || !entityId) return;
    setSaving(true);
    setStatus("");
    const body = {};
    fields.forEach((fld) => {
      body[fld.name] = fld.rich
        ? editorStateToHtml(editorStates[fld.name])
        : values[fld.name] != null ? values[fld.name] : "";
    });

    let url = "/my-jeeva-api/translation-edit/" + entityType + "/" + entityId + "?language=" + language;
    if (procedureId) {
      url += "&procedureId=" + procedureId;
    }

    fetch(url, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.success) {
          setStatus("Saved.");
        } else {
          setStatus((res && res.message) || "Save failed.");
        }
      })
      .catch((err) => {
        console.error("Error saving translation:", err);
        setStatus("Save failed.");
      })
      .finally(() => setSaving(false));
  };

  const overlay = {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.5)", zIndex: 100000,
    display: "flex", alignItems: "center", justifyContent: "center",
  };
  const card = {
    background: "#fff", borderRadius: 8, width: "min(760px, 94vw)",
    maxHeight: "90vh", overflowY: "auto", padding: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>Translations{title ? " — " + title : ""}</h4>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        {languagesError ? (
          <div style={{ padding: 16, background: "#fff3cd", borderRadius: 4, border: "1px solid #ffc107", color: "#856404", marginBottom: 16 }}>
            {languagesError}
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 600, marginRight: 8 }}>Language</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} style={{ padding: "4px 8px" }}>
                {languages.map((l) => {
                  const langRef = ALL_LANGUAGES.find((al) => al.code.toUpperCase() === l.code.toUpperCase());
                  const displayName = langRef ? `${langRef.englishName} (${langRef.displayName})` : (l.englishName ? `${l.englishName} (${l.displayName})` : l.displayName || l.code);
                  return (
                    <option key={l.code} value={l.code.toLowerCase()}>{displayName}</option>
                  );
                })}
              </select>
              {loading && <span style={{ marginLeft: 10, color: "#888" }}>Loading…</span>}
              {autoTranslating && <span style={{ marginLeft: 10, color: "#667eea" }}>Auto-translating…</span>}
            </div>

            {fields.map((f) => (
              <div key={f.name} style={{ marginBottom: 16 }} dir={isRtl ? "rtl" : "ltr"}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>{f.label || f.name}</label>
                {f.rich ? (
                  <Editor
                    editorState={editorStates[f.name] || EditorState.createEmpty()}
                    onEditorStateChange={(es) =>
                      setEditorStates((prev) => Object.assign({}, prev, { [f.name]: es }))
                    }
                    textAlignment={isRtl ? "right" : "left"}
                    wrapperStyle={{ border: "1px solid #ccc", borderRadius: 4 }}
                    editorStyle={{ minHeight: 180, padding: "0 10px", direction: isRtl ? "rtl" : "ltr" }}
                    toolbarStyle={{ marginBottom: 0 }}
                  />
                ) : (
                  <textarea
                    dir={isRtl ? "rtl" : "ltr"}
                    rows={f.rows || 4}
                    value={values[f.name] != null ? values[f.name] : ""}
                    onChange={(e) => setValues(Object.assign({}, values, { [f.name]: e.target.value }))}
                    style={{ width: "100%", padding: 8, boxSizing: "border-box", fontFamily: "inherit", textAlign: isRtl ? "right" : "left" }}
                  />
                )}
              </div>
            ))}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
              {status && <span style={{ color: status === "Saved." ? "#2e7d32" : "#c62828" }}>{status}</span>}
              <button onClick={onClose} style={{ padding: "6px 16px" }}>Close</button>
              <button onClick={save} disabled={saving || loading || !language}
                style={{ padding: "6px 16px", background: "#2679d3", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// src/my-jeeva/projectmgmt/TranslationPanel.js